// SPDX-License-Identifier: AGPL-3.0-or-later

/**
 * How the member list sidebar recovers when a subscription is sent but no member list payload comes
 * back for the requested ranges.
 *
 * The gateway answers a subscribe with a SYNC only when the ranges differ from what it already holds
 * for the session. A client that dropped its cached list while the server kept the subscription can
 * therefore resend the same ranges forever and never be answered. Recovery escalates from plain
 * resends to a gateway-side refresh: an explicit unsubscribe followed by a resubscribe, spaced past the
 * server's request coalescing window so the two are not merged back into a no-op. It never replaces
 * the gateway session, which used to force a full IDENTIFY and reload everything for a sidebar.
 */

/** Delay before resending an unanswered subscription with the same ranges. */
export const MEMBER_LIST_RESUBSCRIBE_DELAY_MS = 1000;
export const MEMBER_LIST_MAX_RESUBSCRIBE_ATTEMPTS = 3;
/** Delay before a gateway-side refresh once plain resends are exhausted. */
export const MEMBER_LIST_REFRESH_DELAY_MS = 3000;
export const MEMBER_LIST_MAX_REFRESH_ATTEMPTS = 2;
/**
 * Gap between the explicit unsubscribe and the resubscribe of a refresh. The gateway coalesces lazy
 * subscribe requests for the same channel for 100ms and would collapse the pair into the resubscribe
 * alone, which it then deduplicates against the ranges it still holds.
 */
export const MEMBER_LIST_REFRESH_RESUBSCRIBE_GAP_MS = 300;

export type MemberListHydrationRecoveryStep = 'resubscribe' | 'refresh' | 'give-up';

export function selectMemberListHydrationRecoveryStep({
	resubscribeAttempts,
	refreshAttempts,
}: {
	resubscribeAttempts: number;
	refreshAttempts: number;
}): MemberListHydrationRecoveryStep {
	if (resubscribeAttempts < MEMBER_LIST_MAX_RESUBSCRIBE_ATTEMPTS) return 'resubscribe';
	if (refreshAttempts < MEMBER_LIST_MAX_REFRESH_ATTEMPTS) return 'refresh';
	return 'give-up';
}
